import { useApiMutation } from "@gladis/api-client";
import {
  EstimateNotice,
  MortgagePartForm,
  MortgagePartList,
  PropertyEditForm,
  PropertyFacts,
  type MortgagePartDto,
  SellPropertyDialog,
  useUpdateEstimate,
  usePropertyDetail,
  ValuationForm,
  ValuationHistory,
} from "@gladis/domain-properties/ui";
import { EXPENSES_KEY, useExpensesSchedule } from "@gladis/domain-expenses/ui";
import { formatEur } from "@gladis/shared";
import { ActionMenu, Button, Dialog, Panel } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

import { Page } from "../../../components/page.js";

export const Route = createFileRoute("/_authenticated/_shell/properties_/$propertyId")({
  component: PropertyDetail,
});

/**
 * One property: what it is worth, and how that has been stated over time.
 *
 * THE `_` IN THE FILENAME OPTS OUT OF NESTING. Without it TanStack makes this a child of
 * `/properties`, which would need an `<Outlet />` there and render the list above the detail. The
 * same note is on the savings per-account route, and it is the kind of thing that costs an hour the
 * first time and no time ever again once written down.
 *
 * ==========================================================================================
 * EVERY ACTION IS IN THE TITLE ROW, AND ONLY ONE OF THEM IS A BUTTON
 * ==========================================================================================
 *
 * They used to be scattered down the page: "Add a mortgage part" inside the mortgage section, "Mark
 * as sold" under a heading of its own, "Archive" under another, and the valuation form permanently
 * expanded in between. A member looking for what they could do here had to scroll to find out, and
 * two of those sections existed only to hold a single button.
 *
 * `Page.Title`'s `actions` slot is where every page in this application puts its controls. Five
 * buttons there would be a worse version of the same problem, so **one primary button and a menu**:
 *
 *   RECORD VALUATION is primary because it is the act this module exists for. A household states
 *   what its house is worth; everything else on this page is derived from those statements or is a
 *   lifecycle event that happens once.
 *
 *   The rest sit behind `ActionMenu`, the control income and investments already use for exactly
 *   this. It also lets the labels stay long enough to mean something — "Mark as sold" and "Archive
 *   this property" have very different consequences and neither abbreviates well.
 *
 * ==========================================================================================
 * DIALOGS ARE MOUNTED ONLY WHILE OPEN
 * ==========================================================================================
 *
 * 016's E2E found the bug this rule exists for: a dialog left mounted keeps the `useState` from the
 * first time it was opened, so a second entry starts with the first one's values still in the
 * fields — a fee, most memorably, which lands in a cost basis and moves an average without appearing
 * to. Income mounts its dialogs the same way for the same reason.
 */
function PropertyDetail(): ReactNode {
  const t = useT();
  const { propertyId } = Route.useParams();
  const detail = usePropertyDetail(propertyId);
  const navigate = useNavigate();

  const [dialog, setDialog] = useState<"valuation" | "mortgage" | "sell" | "edit" | null>(null);
  /** The part being corrected, so the form opens on it rather than empty. */
  const [editingPart, setEditingPart] = useState<MortgagePartDto | null>(null);
  const close = () => {
    setDialog(null);
    setEditingPart(null);
  };

  /*
   * EACH PART'S PAYMENT DATE AND NEXT DUE DATE (feature 024). They are the expenses side's facts — a part
   * becomes a mortgage row there, keyed `mortgage:<partId>` — so this page reads the expenses schedule rather
   * than the properties package learning about due dates. The month read, household-wide, like the Expenses
   * page's own; `currentAnchor` and `nextDue` do not depend on the period.
   */
  const schedule = useExpensesSchedule("month");
  const partNextDue: Record<string, string | null> = Object.fromEntries(
    (detail.data?.mortgageParts ?? []).map((part) => [
      part.id,
      schedule.data?.nextDue[`mortgage:${part.id}`] ?? null,
    ]),
  );

  const updateEstimate = useUpdateEstimate(propertyId);
  const archive = useApiMutation<{ archivedPartIds: string[] }, Record<string, never>>(
    (client) => client.post<{ archivedPartIds: string[] }>(`/properties/${propertyId}/archive`, {}),
    { invalidates: [["properties"]] },
  );

  const property = detail.data?.property;
  const archived = property?.archivedAt !== null;
  const address = property
    ? `${property.address.street} ${property.address.houseNumber}${property.address.houseAddition ?? ""}`
    : t("prop.list.colProperty");

  return (
    <Page>
      <Page.Title
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              disabled={archived}
              onClick={() => {
                setDialog("valuation");
              }}
            >
              {t("prop.valuation.record")}
            </Button>
            <ActionMenu
              label={`Actions for ${address}`}
              items={[
                {
                  label: updateEstimate.isPending
                    ? t("page.property.updating")
                    : t("page.property.updateEstimate"),
                  /*
                   * Hidden by `disabled` rather than absent, unlike the property page's earlier
                   * treatment: in a menu an item that vanishes changes the position of the ones
                   * below it, and a member who used this yesterday reaches for where it was.
                   * `indexable` is false for a non-NL property or an unresolvable address.
                   */
                  disabled: !property?.indexable || archived || updateEstimate.isPending,
                  onSelect: () => {
                    updateEstimate.mutate();
                  },
                },
                {
                  label: t("page.property.editDetails"),
                  disabled: archived,
                  onSelect: () => {
                    setDialog("edit");
                  },
                },
                {
                  label: t("page.property.addMortgagePart"),
                  disabled: archived,
                  onSelect: () => {
                    setDialog("mortgage");
                  },
                },
                {
                  label: t("page.property.markAsSold"),
                  disabled: archived,
                  onSelect: () => {
                    setDialog("sell");
                  },
                },
                {
                  label: t("page.property.archive"),
                  destructive: true,
                  disabled: archived || archive.isPending,
                  onSelect: () => {
                    archive.mutate({} as Record<string, never>, {
                      onSuccess: () => {
                        void navigate({ to: "/properties" });
                      },
                    });
                  },
                },
              ]}
            />
          </div>
        }
      >
        {address}
      </Page.Title>

      <Page.Body>
        <div className="col-span-full flex flex-col gap-6">
          {property ? (
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Figure label={t("page.property.currentValue")} amount={property.currentValue} />
              <Figure label={t("page.property.paid")} amount={property.purchasePrice} />
              {/*
                Value gain only — it deliberately excludes mortgage paydown (FR-037). The card
                answers "did this asset appreciate?"; the household's full equity story belongs to
                the net-worth view, and keeping them distinct keeps both useful.
              */}
              <Figure label={t("page.property.gain")} amount={property.gain} />
            </dl>
          ) : null}

          {property && (
            <Panel title={t("page.property.details")} description={t("page.property.detailsHint")}>
              <PropertyFacts property={property} />
            </Panel>
          )}

          <Panel
            title={t("prop.valuation.heading")}
            description={t("page.property.valuationHistoryHint")}
          >
            {/*
             * The notice sits with the FIGURE it qualifies rather than with the button that changes
             * it. "Estimated since March 2024" is about this table's top row.
             */}
            {property && <EstimateNotice property={property} update={updateEstimate} />}
            <ValuationHistory propertyId={propertyId} />
          </Panel>

          <Panel title={t("exp.list.mortgage")}>
            <MortgagePartList
              propertyId={propertyId}
              nextDue={partNextDue}
              onEdit={(part) => {
                setEditingPart(part);
                setDialog("mortgage");
              }}
            />
          </Panel>
        </div>
      </Page.Body>

      {dialog === "valuation" && (
        <Dialog
          open
          onClose={close}
          title={t("page.property.recordValuation")}
          description={t("page.property.valuationHint")}
        >
          <ValuationForm propertyId={propertyId} onRecorded={close} />
        </Dialog>
      )}

      {dialog === "mortgage" && (
        <Dialog
          open
          onClose={close}
          title={
            editingPart ? t("page.property.editMortgagePart") : t("page.property.addMortgagePart")
          }
          description={t("page.property.mortgageHint")}
        >
          <MortgagePartForm
            propertyId={propertyId}
            part={editingPart ?? undefined}
            onAdded={close}
            paymentDate={{
              current:
                editingPart === null
                  ? null
                  : (schedule.data?.currentAnchor[`mortgage:${editingPart.id}`] ?? null),
              save: (client, partId, firstPaymentDate) =>
                client.put<void>(`/expenses/derived/${partId}/payment-date`, {
                  firstPaymentDate,
                }),
              invalidates: [EXPENSES_KEY],
            }}
          />
        </Dialog>
      )}

      {dialog === "edit" && property && (
        <Dialog
          open
          onClose={close}
          title={t("page.property.editProperty")}
          description={t("page.property.editHint")}
          size="wide"
        >
          <PropertyEditForm propertyId={propertyId} property={property} onSaved={close} />
        </Dialog>
      )}

      {dialog === "sell" && (
        <Dialog
          open
          onClose={close}
          title={t("page.property.markAsSold")}
          description={t("page.property.soldHint")}
        >
          <SellPropertyDialog
            propertyId={propertyId}
            onSold={() => {
              close();
              void navigate({ to: "/properties" });
            }}
            onCancel={close}
          />
        </Dialog>
      )}
    </Page>
  );
}

function Figure({ label, amount }: { label: string; amount: number }): ReactNode {
  return (
    <div className="rounded-control border-line bg-panel border p-4">
      <dt className="text-muted text-sm">{label}</dt>
      <dd className="text-2xl font-semibold tabular-nums">{formatEur(amount)}</dd>
    </div>
  );
}
